import { FootprintPolygonsType, PositionType } from 'data/types';
import polygonClipping, { MultiPolygon, Polygon } from 'polygon-clipping';

export const INTENTIONAL_VOID_THRESHOLD_SQFT = 150;

const SHARED_BOUNDARY_PERP_EPS = 0.1;
const ZERO_LENGTH_EPS = 1e-9;
const FOOTPRINT_TOUCH_MIN_LEN_FT = 0.5;

function polygonToClipping(vertices: PositionType[]): Polygon {
  const ring = vertices.map((v) => [v.x, v.y] as [number, number]);
  return [[...ring, ring[0]]];
}

function clippingToPolygons(mp: MultiPolygon): PositionType[][] {
  const result: PositionType[][] = [];
  for (const poly of mp) {
    const outer = poly[0];
    if (outer && outer.length >= 4) {
      const points = outer.slice(0, -1).map(([x, y]) => ({ x, y }));
      if (points.length >= 3) {
        result.push(points);
      }
    }
  }
  return result;
}

export function polygonAreaAbs(vertices: PositionType[]): number {
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i += 1) {
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    area += a.x * b.y - b.x * a.y;
  }
  return Math.abs(area) / 2;
}

function sharedSegmentLength(
  a1: PositionType,
  a2: PositionType,
  b1: PositionType,
  b2: PositionType
): number {
  const dxA = a2.x - a1.x;
  const dyA = a2.y - a1.y;
  const lenA = Math.hypot(dxA, dyA);
  if (lenA < ZERO_LENGTH_EPS) return 0;

  const dirX = dxA / lenA;
  const dirY = dyA / lenA;

  const perpDistB1 = Math.abs((b1.x - a1.x) * dirY - (b1.y - a1.y) * dirX);
  const perpDistB2 = Math.abs((b2.x - a1.x) * dirY - (b2.y - a1.y) * dirX);
  if (
    perpDistB1 > SHARED_BOUNDARY_PERP_EPS ||
    perpDistB2 > SHARED_BOUNDARY_PERP_EPS
  ) {
    return 0;
  }

  const t1 = (b1.x - a1.x) * dirX + (b1.y - a1.y) * dirY;
  const t2 = (b2.x - a1.x) * dirX + (b2.y - a1.y) * dirY;

  const tStart = Math.max(0, Math.min(t1, t2));
  const tEnd = Math.min(lenA, Math.max(t1, t2));

  return Math.max(0, tEnd - tStart);
}

export function sharedBoundaryLength(
  polyA: PositionType[],
  polyB: PositionType[]
): number {
  if (polyA.length < 2 || polyB.length < 2) return 0;
  let total = 0;
  const nA = polyA.length;
  const nB = polyB.length;
  for (let i = 0; i < nA; i += 1) {
    const a1 = polyA[i];
    const a2 = polyA[(i + 1) % nA];
    for (let j = 0; j < nB; j += 1) {
      const b1 = polyB[j];
      const b2 = polyB[(j + 1) % nB];
      total += sharedSegmentLength(a1, a2, b1, b2);
    }
  }
  return total;
}

export function findBestRoomForVoid(
  voidPolygon: PositionType[],
  roomPolygons: PositionType[][]
): number {
  let bestIdx = -1;
  let bestLen = 0;
  for (let i = 0; i < roomPolygons.length; i += 1) {
    const len = sharedBoundaryLength(roomPolygons[i], voidPolygon);
    if (len > bestLen) {
      bestLen = len;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function computeUncoveredVoids(
  roomPolygons: PositionType[][],
  footprints: FootprintPolygonsType
): PositionType[][] {
  if (roomPolygons.length === 0) return [];
  const footprintClip: MultiPolygon = footprints
    .filter((footprint) => footprint.length >= 3)
    .map(polygonToClipping);
  if (footprintClip.length === 0) return [];

  const roomClips = roomPolygons.map(polygonToClipping);

  const [first, ...rest] = roomClips;
  const roomsUnion = polygonClipping.union(first, ...rest);
  const voidsMP = polygonClipping.difference(footprintClip, ...roomsUnion);

  return clippingToPolygons(voidsMP);
}

export function absorbVoidIntoRoom(
  roomPolygon: PositionType[],
  voidPolygon: PositionType[]
): PositionType[] | null {
  if (roomPolygon.length < 3 || voidPolygon.length < 3) return null;

  const merged = polygonClipping.union(
    polygonToClipping(roomPolygon),
    polygonToClipping(voidPolygon)
  );
  const polygons = clippingToPolygons(merged);
  if (polygons.length === 0) return null;

  let largest = polygons[0];
  let largestArea = polygonAreaAbs(largest);
  for (let i = 1; i < polygons.length; i += 1) {
    const area = polygonAreaAbs(polygons[i]);
    if (area > largestArea) {
      largestArea = area;
      largest = polygons[i];
    }
  }
  return largest;
}

export function polygonPerimeter(vertices: PositionType[]): number {
  let perimeter = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i += 1) {
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    perimeter += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return perimeter;
}

export function polygonCompactness(vertices: PositionType[]): number {
  const perimeter = polygonPerimeter(vertices);
  if (perimeter <= 0) return 0;
  const area = polygonAreaAbs(vertices);
  return (4 * Math.PI * area) / (perimeter * perimeter);
}

export function isVoidTouchingFootprint(
  voidPolygon: PositionType[],
  footprint: PositionType[]
): boolean {
  if (voidPolygon.length < 2 || footprint.length < 2) return false;
  const nV = voidPolygon.length;
  const nF = footprint.length;
  for (let i = 0; i < nV; i += 1) {
    const v1 = voidPolygon[i];
    const v2 = voidPolygon[(i + 1) % nV];
    for (let j = 0; j < nF; j += 1) {
      const f1 = footprint[j];
      const f2 = footprint[(j + 1) % nF];
      if (sharedSegmentLength(v1, v2, f1, f2) >= FOOTPRINT_TOUCH_MIN_LEN_FT) {
        return true;
      }
    }
  }
  return false;
}

export function findBestRoomForVoidByCompactness(
  voidPolygon: PositionType[],
  roomPolygons: PositionType[][]
): number {
  let bestIdx = -1;
  let bestCompactness = -Infinity;
  for (let i = 0; i < roomPolygons.length; i += 1) {
    if (sharedBoundaryLength(roomPolygons[i], voidPolygon) > 0) {
      const merged = absorbVoidIntoRoom(roomPolygons[i], voidPolygon);
      if (merged) {
        const compactness = polygonCompactness(merged);
        if (compactness > bestCompactness) {
          bestCompactness = compactness;
          bestIdx = i;
        }
      }
    }
  }
  return bestIdx;
}
